import { Controller } from 'stimulus';
import { templateContent } from '@/js/util';
import { modalContainer } from '@/js/modules/modal-mount';

// Backstop for an import whose finish broadcast never arrives (dropped job,
// closed WebSocket) so its tile stops coming back on later grid renders.
const PENDING_TILE_TTL = 5 * 60 * 1000;

const LIBRARY_GRID_ID = 'sidepanel_items_library';
const LIBRARY_EMPTY_ID = `${LIBRARY_GRID_ID}-empty`;

class TabTrigger {
  constructor(element) {
    this.element = element;
  }

  is(other) {
    return this.element === other.element;
  }

  set active(value) {
    this.element.classList.toggle('bg-df-heaven-gates-blue', value);
    this.element.classList.toggle('bg-white', !value);
  }

  get tabName() {
    return this.element.value;
  }
}

export default class extends Controller {
  static targets = ['tabTrigger', 'tab', 'form', 'loadingItemTemplate', 'urlImportTemplate'];

  // Tile id -> insertion time, for URL imports still in flight. Held here
  // rather than in the grid because every filters submit replaces the grid's
  // HTML, taking any tile parked inside it with it.
  #pendingTiles = new Map();

  connect() {
    const activeTab = this.activeTab;
    if (activeTab) {
      this.triggers.forEach((trigger) => {
        trigger.active = trigger.tabName === this.#tabName(activeTab);
      });
    }
  }

  /**
   * @param {MouseEvent} e
   */
  switch({ currentTarget }) {
    const currentTrigger = new TabTrigger(currentTarget);

    const actions = {
      library: 'library_tab_opened',
      project_items: 'project_items_tab_opened',
      asset: 'assets_tab_opened',
    };
    const action = actions[currentTrigger.tabName];
    if (action) {
      window.ed.featureActionsService.add(action);
    }

    this.tabTargets.forEach((tab) => {
      tab.classList.toggle('hidden', this.#tabName(tab) !== currentTrigger.tabName);
    });

    this.triggers.forEach((trigger) => {
      trigger.active = currentTrigger.is(trigger);
    });
  }

  switchFromDetail(event) {
    const tabName = event.detail?.tabName || 'library';
    // The uploader offers Asset on every plan, but the Assets tab is shown only on some of them.
    const trigger = this.triggers.find((t) => t.tabName === tabName);
    if (!trigger) {
      return;
    }

    this.switch({ currentTarget: trigger.element });
  }

  requestManualUpload() {
    this.dispatch('manual-upload-requested');

    setTimeout(() => {
      this.requestTabNamePropagatedEvent();
      this.requestManualSourceTagEvent();
    });
  }

  requestUrlImport() {
    this.dispatch('url-import-requested');
    window.ed.featureActionsService.add('url_import_modal_opened');

    modalContainer().appendChild(templateContent(this.urlImportTemplateTarget));
  }

  /**
   * Registers an in-flight URL import and injects its generated DOM id into the
   * submitting form's `loading_tile_id` hidden field.
   *
   * Wired as a document-level action from the URL-import modal form's submit
   * event so the tile only appears when the user actually submits — not when
   * the modal opens.
   *
   * The tile is registered here but drawn by #syncLoadingTiles, which also
   * keeps it drawn across grid renders until the import settles.
   *
   * @param {SubmitEvent} event
   */
  insertLoadingTile(event) {
    const form = event.target;
    const hidden = form.querySelector('input[name="loading_tile_id"]');
    if (!hidden) {
      return;
    }

    window.ed?.featureActionsService.add('url_import_submitted');

    const tileId = `url-import-loading-${crypto.randomUUID()}`;
    hidden.value = tileId;
    this.#pendingTiles.set(tileId, Date.now());
    this.#syncLoadingTiles();
  }

  /**
   * A finished import arrives as a Turbo Stream aimed at the tile's id —
   * `replace` on success, `remove` on failure. Either way the import is over,
   * so stop re-deriving a tile for it: the product Turbo just dropped into the
   * grid is an ordinary item from here on, and the next filters submit decides
   * whether it still belongs in the list.
   *
   * @param {CustomEvent} event `turbo:custom:stream-render`, from the `<turbo-stream>` that has
   *   just rendered
   */
  settleLoadingTile({ target: stream }) {
    // Dropped only once this stream has actually rendered. It and a grid
    // re-render both defer through nextRepaint, and a grid render going first
    // wipes the tile out; staying pending until then lets the post-render
    // re-sync put the tile back for this stream to land on.
    if (!this.#pendingTiles.delete(stream.target)) {
      return;
    }

    // A failed import leaves the grid as empty as it found it, so the message
    // this tile was hiding has to come back.
    this.#toggleEmptyMessage();
  }

  /**
   * The library grid is a server-owned fragment: a filters submit replaces its
   * contents wholesale, taking any loading tile inside it along. Turbo defers
   * that mutation by an animation frame (`StreamElement#render` awaits
   * `nextRepaint`), so the tiles have to be re-derived from the stream's own
   * render — hooking the request's completion instead lands a frame early, and
   * the tile is prepended into content that is about to be thrown away.
   *
   * @param {CustomEvent} event `turbo:custom:stream-render`, from the `<turbo-stream>` that has
   *   just rendered
   */
  resyncAfterGridRender({ target: stream }) {
    if (stream.target === LIBRARY_GRID_ID) {
      this.#syncLoadingTiles();
    }
  }

  requestTabNamePropagatedEvent() {
    const activeTab = this.activeTab;
    const activeTabName = this.#tabName(activeTab);

    this.dispatch('tab-name-propagated', {
      detail: { value: activeTabName },
    });
  }

  requestManualSourceTagEvent() {
    this.dispatch('upload-source-propagated', { detail: { value: 'manual' } });
  }

  submit({ detail }) {
    const tabName = detail?.tabName;
    if (tabName !== 'all') {
      const tab = tabName ? this.#findTabByName(tabName) : this.activeTab;
      tab.querySelector('.sidepanel-items-scrollable').classList.add('hidden');
      tab.querySelector('.item-skeletons').classList.remove('hidden');

      this.#submitByTab(tab);
    } else {
      this.tabTargets.forEach((tab) => this.#submitByTab(tab));
    }
  }

  shoppableUpdated({ detail }) {
    if (detail.kind === 'item') {
      this.submit({ detail: { tabName: 'all' } });
    }
  }

  hideItemSkeletons(e) {
    e.target.parentNode.querySelector('.sidepanel-items-scrollable').classList.remove('hidden');
    e.target.parentNode.querySelector('.item-skeletons').classList.add('hidden');
  }

  /**
   * @return {Array<TabTrigger>}
   */
  get triggers() {
    return this.tabTriggerTargets.map((el) => new TabTrigger(el));
  }

  /**
   * @return {HTMLElement}
   */
  get activeTab() {
    return this.tabTargets.find((tab) => !tab.classList.contains('hidden'));
  }

  /**
   * @param {HTMLElement} tab
   * @return {String}
   */
  #tabName(tab) {
    return tab.getAttribute(`data-${this.identifier}-tab`);
  }

  #findTabByName(name) {
    return this.tabTargets.find((tab) => this.#tabName(tab) === name);
  }

  #submitByTab(tab) {
    const targetForm = this.formTargets.find((form) => tab.contains(form));
    targetForm?.requestSubmit();
  }

  /**
   * Re-derives the loading tiles the library grid should be showing: one per
   * in-flight import, on top, whatever the list is filtered to. An import
   * deliberately outranks the filter — the user asked for it by pasting a URL,
   * so they get to watch it arrive even inside a search it will not match.
   *
   * Called on submit and after every grid render, so a tile survives a grid the
   * server has re-rendered underneath it.
   */
  #syncLoadingTiles() {
    this.#pruneStaleTiles();

    if (!this.hasLoadingItemTemplateTarget) {
      return;
    }

    const grid = document.getElementById(LIBRARY_GRID_ID);
    if (!grid) {
      return;
    }

    this.#pendingTiles.forEach((_insertedAt, id) => {
      if (document.getElementById(id)) {
        return;
      }

      const tile = templateContent(this.loadingItemTemplateTarget);
      tile.id = id;
      grid.prepend(tile);
    });

    this.#toggleEmptyMessage();
  }

  /**
   * "No items found" reads as a lie next to a tile that is visibly loading one,
   * or next to the product that tile just became. Keyed off what the grid is
   * actually holding rather than off the pending count, so a failed import —
   * which empties the grid again — brings the message back, while one that
   * failed alongside a product that did arrive does not.
   */
  #toggleEmptyMessage() {
    const grid = document.getElementById(LIBRARY_GRID_ID);

    document
      .getElementById(LIBRARY_EMPTY_ID)
      ?.classList.toggle('hidden', Boolean(grid?.firstElementChild));
  }

  #pruneStaleTiles() {
    const cutoff = Date.now() - PENDING_TILE_TTL;

    this.#pendingTiles.forEach((insertedAt, id) => {
      if (insertedAt < cutoff) {
        this.#pendingTiles.delete(id);
        document.getElementById(id)?.remove();
      }
    });
  }
}
